import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Typography, Button, Spin, Result, Space, Tag, message } from "antd";
import { TeamOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../config/api";
import "./main.css";

const { Title, Text } = Typography;

function TeamInvite() {
  const { token: invitationId } = useParams();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [done, setDone] = useState(null);

  const baseURL = API_BASE_URL;

  useEffect(() => {
    // Validate the invitation
    fetch(`${baseURL}/team/invite/${invitationId}`)
      .then(res => res.json())
      .then(data => {
        setLoading(false);
        if (data.message === "Success") {
          setInvite(data);
        } else {
          setError(data.message || "Invalid or expired invitation");
        }
      })
      .catch(() => {
        setLoading(false);
        setError("Failed to validate invitation. Please try again.");
      });
  }, [invitationId]);

  const handleAccept = () => {
    setAccepting(true);
    fetch(`${baseURL}/team/invite/${invitationId}/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      }
    })
      .then(res => res.json())
      .then(data => {
        setAccepting(false);
        if (data.message === "Invitation accepted") {
          // Auto-login with the returned token
          if (data.data?.token) {
            localStorage.setItem("token", data.data.token);
            const decoded = jwtDecode(data.data.token);
            const userData = {
              ...decoded.default_page || {},
              user_id: decoded.user_id,
              name: decoded.name,
              emailid: decoded.emailid,
              workspace_id: decoded.workspace_id,
              workspace_role: decoded.workspace_role
            };
            localStorage.setItem("userData", JSON.stringify(userData));
          }
          if (data.workspace_id) {
            localStorage.setItem("current_workspace_id", data.workspace_id);
          }
          setDone("accepted");
        } else {
          messageApi.error(data.message || "Failed to accept invitation");
        }
      })
      .catch(() => {
        setAccepting(false);
        messageApi.error("Something went wrong. Please try again.");
      });
  };

  const handleDecline = () => {
    setDeclining(true);
    fetch(`${baseURL}/team/invite/${invitationId}/decline`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    })
      .then(res => res.json())
      .then(data => {
        setDeclining(false);
        if (data.message === "Invitation declined") {
          setDone("declined");
        } else {
          messageApi.error(data.message || "Failed to decline invitation");
        }
      })
      .catch(() => {
        setDeclining(false);
        messageApi.error("Something went wrong. Please try again.");
      });
  };

  if (loading) {
    return (
      <div className="team-invite-container">
        <Spin size="large" tip="Validating invitation..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-invite-container">
        <Result
          status="warning"
          title="Invitation Unavailable"
          subTitle={error}
          extra={<Button type="primary" onClick={() => navigate("/login")}>Go to Login</Button>}
        />
      </div>
    );
  }

  if (done === "accepted") {
    return (
      <div className="team-invite-container">
        <Result
          status="success"
          title="You've joined the workspace!"
          subTitle={`You are now a member of "${invite?.workspace_name}".`}
          extra={<Button type="primary" onClick={() => navigate("/ig")}>Go to Dashboard</Button>}
        />
      </div>
    );
  }

  if (done === "declined") {
    return (
      <div className="team-invite-container">
        <Result
          status="info"
          title="Invitation Declined"
          subTitle="You have declined this invitation."
          extra={<Button type="primary" onClick={() => navigate("/login")}>Go to Login</Button>}
        />
      </div>
    );
  }

  return (
    <div className="team-invite-container">
      {contextHolder}
      <Card className="team-invite-card" bordered>
        <TeamOutlined className="team-invite-icon" />
        <Title level={3}>You're Invited!</Title>
        <Text type="secondary">
          {invite?.invited_by_name ? `${invite.invited_by_name} has` : "You've been"} invited you to join a team workspace.
        </Text>

        <div className="team-invite-details">
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div><Text strong>Workspace:</Text> <Text>{invite?.workspace_name}</Text></div>
            <div><Text strong>Role:</Text> <Tag color="blue">{invite?.role?.toUpperCase()}</Tag></div>
            <div><Text strong>Email:</Text> <Text>{invite?.email}</Text></div>
            {invite?.expires_at && (
              <div><Text strong>Expires:</Text> <Text type="secondary">{new Date(invite.expires_at).toLocaleDateString()}</Text></div>
            )}
          </Space>
        </div>

        <div className="team-invite-actions">
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            loading={accepting}
            onClick={handleAccept}
          >
            Accept Invitation
          </Button>
          <Button
            danger
            size="large"
            icon={<CloseCircleOutlined />}
            loading={declining}
            onClick={handleDecline}
          >
            Decline
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default TeamInvite;
